/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {LocalDate} from 'spine-web/proto/spine/time/time_pb';
import LocalDateExt from '@/shell/model/spine/time/LocalDateExt';

/**
 * An abstract notion of a date range. This is a base class for a set of different types of date
 * ranges used in the `DateRangeMenu` component.
 */
export abstract class DateRange {

  /**
   * The start date of the date range (inclusive). A value of `null` means an open range (any date
   * in the past).
   */
  abstract get fromDate(): LocalDate | null;

  /**
   * The end date of the date range (inclusive). A value of `null` means an open range (any date
   * in the future).
   */
  abstract get toDate(): LocalDate | null;

  /**
   * A string that identifies this type of date range.
   */
  abstract get rangeType(): string;

  /**
   * @param date a date to be checked
   * @return `true`, if the provided date belongs to the range defined by this object, and
   *         `false` otherwise
   */
  contains(date: LocalDate): boolean {
    const dateExt = LocalDateExt.of(date);
    const fromDateExt = this.fromDate ? LocalDateExt.of(this.fromDate) : null;
    const toDateExt = this.toDate ? LocalDateExt.of(this.toDate) : null;
    const afterStart = fromDateExt == null || fromDateExt.until(dateExt) >= 0;
    const beforeEnd = toDateExt == null || dateExt.until(toDateExt) >= 0;
    return afterStart && beforeEnd;
  }
}

/**
 * An implementation of `DateRange` that represents the unlimited range that extends infinitely
 * into the past and into the future.
 */
export class AllTimeDateRange extends DateRange {

  /**
   * A value returned by the `rangeType()` method of this class.
   */
  static readonly TYPE = 'AllTimeDateRange'

  get rangeType(): string {
    return AllTimeDateRange.TYPE;
  }

  get fromDate(): LocalDate | null {
    return null;
  }

  get toDate(): LocalDate | null {
    return null;
  }
}

/**
 * A kind of `DateRange` that represents a single day.
 */
export abstract class SingleDayDateRange extends DateRange {

  /**
   * A date that this date range represents.
   */
  protected abstract get day(): LocalDate;

  get fromDate(): LocalDate | null {
    return this.day;
  }

  get toDate(): LocalDate | null {
    return this.day;
  }
}

/**
 * An implementation of `DateRange` that represents a single day of today.
 */
export class TodayDateRange extends SingleDayDateRange {
  get rangeType(): string {
    return 'TodayDateRange';
  }

  protected get day(): LocalDate {
    return LocalDateExt.today().value;
  }
}

/**
 * An implementation of `DateRange` that represents a single day of yesterday.
 */
export class YesterdayDateRange extends SingleDayDateRange {
  get rangeType(): string {
    return 'YesterdayDateRange';
  }

  protected get day(): LocalDate {
    return LocalDateExt.today()
                       .addDays(-1).value;
  }
}

/**
 * An implementation of `DateRange` that represents the current calendar week. A calendar week here
 * means a Sunday to Saturday week (or Monday to Sunday, depending on the current browser's locale).
 */
export class ThisWeekDateRange extends DateRange {
  private readonly includeFutureDays: boolean;

  /**
   * @param includeFutureDays `true` makes the full week to be included in this date range (even
   *                          the days after today), and `false` excludes the days after today from
   *                          the resulting range.
   */
  constructor(includeFutureDays: boolean) {
    super();
    this.includeFutureDays = includeFutureDays;
  }

  get rangeType(): string {
    return 'ThisWeekDateRange';
  }

  get fromDate(): LocalDate {
    const today = LocalDateExt.today();
    const todayDate = today.toDate();
    const firstDayOfWeek = today.addDays(-todayDate.getDay());
    return firstDayOfWeek.value;
  }

  get toDate(): LocalDate {
    if (!this.includeFutureDays) {
      return LocalDateExt.today().value;
    }
    return LocalDateExt.of(this.fromDate)
                       .addDays(6).value;
  }
}

/**
 * An implementation of `DateRange` that represents 7 most recent full days up to yesterday,
 * plus today.
 */
export class WeekToNowDateRange extends DateRange {
  get rangeType(): string {
    return 'WeekToNowDateRange';
  }

  get fromDate(): LocalDate {
    const today = LocalDateExt.today();
    return today.addDays(
        -1 /* start counting from yesterday */
        - 7 /* count one week */
    ).value;
  }

  get toDate(): LocalDate {
    return LocalDateExt.today().value;
  }
}

/**
 * An implementation of `DateRange` that represents the current calendar month.
 */
export class ThisMonthDateRange extends DateRange {
  private readonly includeFutureDays: boolean;

  /**
   * @param includeFutureDays `true` makes the full month to be included in this date range (even
   *                          the days after today), and `false` excludes the days after today from
   *                          the resulting range.
   */
  constructor(includeFutureDays: boolean) {
    super();
    this.includeFutureDays = includeFutureDays;
  }

  get rangeType(): string {
    return 'ThisMonthDateRange';
  }

  get fromDate(): LocalDate {
    const today = LocalDateExt.today();
    const d = today.toDate();
    d.setDate(1);
    return LocalDateExt.fromDate(d).value;
  }

  get toDate(): LocalDate {
    if (!this.includeFutureDays) {
      return LocalDateExt.today().value;
    }
    const d = LocalDateExt.of(this.fromDate).toDate();
    d.setMonth(d.getMonth() + 1);
    d.setDate(d.getDate() - 1);
    return LocalDateExt.fromDate(d).value;
  }
}

/**
 * An implementation of `DateRange` that represents a month-sized date interval up to yesterday,
 * plus one day of today.
 *
 * E.g., if today is March 15, 2021, it will represent a range of [February 14 .. March 15, 2021].
 * In special cases when the same date does not exist on the previous month, this method chooses
 * whatever date is considered "one month behind" using the JavaScript `Date` API.
 */
export class MonthToNowDateRange extends DateRange {
  get rangeType(): string {
    return 'MonthToNowDateRange';
  }

  get fromDate(): LocalDate {
    const today = LocalDateExt.today();
    return today.addDays(-1 /* start counting from yesterday */)
                .addMonths(-1).value;
  }

  get toDate(): LocalDate {
    return LocalDateExt.today().value;
  }
}

/**
 * An implementation of `DateRange` that represents the current quarter.
 */
export class ThisQuarterDateRange extends DateRange {
  private readonly includeFutureDays: boolean;

  /**
   * @param includeFutureDays `true` makes the full quarter to be included in this date range (even
   *                          the days after today), and `false` excludes the days after today from
   *                          the resulting range.
   */
  constructor(includeFutureDays: boolean) {
    super();
    this.includeFutureDays = includeFutureDays;
  }

  get rangeType(): string {
    return 'ThisQuarterDateRange';
  }

  get fromDate(): LocalDate {
    const d = LocalDateExt.today()
                          .toDate();
    d.setDate(1);
    const zeroBasedQuarter = Math.floor(d.getMonth() / 3);
    d.setMonth(zeroBasedQuarter * 3);
    return LocalDateExt.fromDate(d).value;
  }

  get toDate(): LocalDate {
    if (!this.includeFutureDays) {
      return LocalDateExt.today().value;
    }
    const d = LocalDateExt.of(this.fromDate).toDate();
    d.setMonth(d.getMonth() + 3);
    d.setDate(d.getDate() - 1);
    return LocalDateExt.fromDate(d).value;
  }
}

/**
 * An implementation of `DateRange` that represents the current calendar year.
 *
 * The start of the period is always January 1 of the current year, and the end of the period
 * (inclusive) is either December 31 of the current year (if the `includeFutureDays` constructor
 * parameter is `true`), or today (if `includeFutureDays` is `false`).
 */
export class ThisYearDateRange extends DateRange {
  private readonly includeFutureDays: boolean;

  /**
   * @param includeFutureDays `true` makes the full year to be included in this date range (even
   *                          the days after today), and `false` excludes the days after today from
   *                          the resulting range.
   */
  constructor(includeFutureDays: boolean) {
    super();
    this.includeFutureDays = includeFutureDays;
  }

  get rangeType(): string {
    return 'ThisYearDateRange';
  }

  get fromDate(): LocalDate {
    const d = LocalDateExt.today()
                          .toDate();
    d.setDate(1);
    d.setMonth(0);
    return LocalDateExt.fromDate(d).value;
  }

  get toDate(): LocalDate {
    if (!this.includeFutureDays) {
      return LocalDateExt.today().value;
    }
    const d = LocalDateExt.of(this.fromDate).toDate();
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return LocalDateExt.fromDate(d).value;
  }
}

/**
 * An implementation of `DateRange`
 */
export class CustomDateRange extends DateRange {
  private readonly _fromDate: LocalDate;
  private readonly _toDate: LocalDate;

  constructor(fromDate: LocalDate, toDate: LocalDate) {
    super();
    this._fromDate = fromDate;
    this._toDate = toDate;
  }

  get rangeType(): string {
    return 'CustomDateRange';
  }

  get fromDate(): LocalDate {
    return this._fromDate;
  }

  get toDate(): LocalDate {
    return this._toDate;
  }
}
